import axios from "./axiosInstance";
import config from "./config";
import { getAuthToken } from "./apiHelpers";

const BASE_URL = config.BASE_URL;

export interface MyPermissionsResponse {
  success: boolean;
  isElevated: boolean;
  permissions: string[];
}

/**
 * Fetch the current user's permission list. Powers the useHasPermission
 * hook. Mirrors the bypass logic in the backend's requirePermission
 * middleware: elevated roles return { isElevated: true, permissions: [] }
 * and the UI treats them as having every permission.
 */
export const getMyPermissions = async (): Promise<MyPermissionsResponse> => {
  const token = getAuthToken();
  const res = await axios.get(`${BASE_URL}/auth/my-permissions`, {
    headers: {
      "Content-Type": "application/json",
      authorization: `Bearer ${token}`,
    },
  });
  return res.data;
};

export interface Capabilities {
  role: string | null;
  source: string | null;
  is_elevated: boolean;
  is_staff: boolean;
  is_member: boolean;
  can_enroll_any_program: boolean;
  permissions: string[];
  /**
   * LAYERED permissions (checked on top of an older one) that have no row in
   * this club yet: the server lets them step aside, so the UI must too
   * (useHasLayeredPermission). Absent from older servers: treated as none.
   */
  undefined_permissions?: string[];
}

/**
 * Fetch the full server-derived authz summary (role flags + permissions)
 * for the current caller — GET /auth/capabilities. Supersedes the
 * my-permissions call: one request gives everything the client used to
 * derive from localStorage role strings. The same object also arrives in
 * the login response (`capabilities`). Returns null on failure.
 */
export const getCapabilities = async (): Promise<Capabilities | null> => {
  const token = getAuthToken();
  const res = await axios.get(`${BASE_URL}/auth/capabilities`, {
    headers: {
      "Content-Type": "application/json",
      authorization: `Bearer ${token}`,
    },
  });
  return (res?.data?.data as Capabilities) || null;
};

export interface MyTenant {
  slug: string;
  name: string;
  status?: string;
}

/**
 * Tenants the central-auth caller has access to (CC drawer tenant
 * switcher). Tenant-auth users get an empty list. Listing only —
 * actual access is enforced per-request by the backend regardless.
 */
export const getMyTenants = async (): Promise<MyTenant[]> => {
  try {
    const token = getAuthToken();
    const res = await axios.get(`${BASE_URL}/auth/my-tenants`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    });
    return res.data?.tenants || [];
  } catch {
    return [];
  }
};
